import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import POSOverrideModal from '../components/pos/POSOverrideModal';
import { useAuth } from './AuthContext';
import { usePOS } from './POSContext';

interface OverrideContextType {
    requirePermission: (permission: string, actionName: string, onSuccess: () => void) => void;
}

const OverrideContext = createContext<OverrideContextType | undefined>(undefined);

export const OverrideProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();

    // Since we fixed App.tsx and POSLayout.tsx, OverrideProvider is safely inside POSProvider.
    const pos = usePOS();
    const posRestaurantId = pos.restaurantId || '';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<{
        permission: string;
        actionName: string;
        onSuccess: () => void;
    } | null>(null);

    // Native check logic (copied primarily from usePermission to keep it unified)
    const hasNativePermission = useCallback((permission: string): boolean => {
        if (!user) return false;
        if (['super_admin', 'admin', 'manager', 'partner'].includes(user.role)) return true;
        return user.permissions?.includes(permission) || false;
    }, [user]);

    const requirePermission = useCallback((permission: string, actionName: string, onSuccess: () => void) => {
        if (hasNativePermission(permission)) {
            // User already has the right, execute immediately
            onSuccess();
        } else {
            // User lacks the right, open the override modal
            setPendingAction({ permission, actionName, onSuccess });
            setIsModalOpen(true);
        }
    }, [hasNativePermission]);

    const handleModalClose = () => {
        setIsModalOpen(false);
        setPendingAction(null);
    };

    const handleOverrideSuccess = () => {
        if (pendingAction) {
            pendingAction.onSuccess();
        }
    };

    return (
        <OverrideContext.Provider value={{ requirePermission }}>
            {children}

            <POSOverrideModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSuccess={handleOverrideSuccess}
                actionName={pendingAction?.actionName}
                requiredPermission={pendingAction?.permission}
                restaurantId={posRestaurantId || localStorage.getItem('pos_restaurant_id') || user?.restaurant_id?.toString() || ''}
            />
        </OverrideContext.Provider>
    );
};

export const useOverride = () => {
    const context = useContext(OverrideContext);
    if (!context) {
        throw new Error('useOverride must be used within an OverrideProvider');
    }
    return context;
};
